import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Button,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import './FontManagement.css';
import { getApiUrl } from '../utils/api';
import { useProject } from '../contexts/ProjectContext';

interface FontInfo {
  name: string;
  file: File | null;
  url: string;
  enabled: boolean;
}

interface FontManagementProps {
  onFontListChange: (fonts: FontInfo[]) => void;
  initialFonts?: FontInfo[];
  onFontUploaded?: (fontName: string, serverUrl: string) => void;
}

const FontManagement: React.FC<FontManagementProps> = ({ onFontListChange, initialFonts = [], onFontUploaded }) => {
  const { selectedProject } = useProject();
  const [fonts, setFonts] = useState<FontInfo[]>(initialFonts);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [fontSearch, setFontSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLicenseDialogOpen, setIsLicenseDialogOpen] = useState<boolean>(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  // Keep internal state in sync if parent updates initialFonts after mount
  useEffect(() => {
    setFonts(initialFonts);
  }, [initialFonts]);

  const uploadFontToAPI = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        `${getApiUrl()}/upload-font?projectId=${selectedProject!.id}`,
        { method: "POST", body: formData }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      if (result.status === "OK") {
        setError(null);
        console.log("Font uploaded and loaded:", file.name);
        // Build server font URL as: <server-origin>/fonts/<filename>
        const serverFontUrl = `${window.location.origin}/fonts/${encodeURIComponent(file.name)}`;
        console.log("Server font URL:", serverFontUrl);
        if (onFontUploaded) {
          onFontUploaded(file.name.replace(/\.[^/.]+$/, ''), serverFontUrl);
        }
      } else {
        setError(result.message || "Unknown error occurred");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload font");
    } finally {
      setLoading(false);
    }
  };

  const processFontFiles = (files: File[]): void => {
    const existingNamesLower: Set<string> = new Set(fonts.map((f) => f.name.toLowerCase()));
    const addedNamesLower: Set<string> = new Set<string>();
    const newFonts: FontInfo[] = [];

    files.forEach((file: File) => {
      const fontName: string = file.name.replace(/\.[^/.]+$/, '');
      const nameLower: string = fontName.toLowerCase();

      if (existingNamesLower.has(nameLower) || addedNamesLower.has(nameLower)) {
        return;
      }

      addedNamesLower.add(nameLower);
      const url: string = URL.createObjectURL(file);

      try {
        const fontFace = new FontFace(fontName, `url(${url})`);
        fontFace.load().then((loadedFace) => (document as any).fonts.add(loadedFace));
      } catch {}

      uploadFontToAPI(file);

      newFonts.push({ name: fontName, file, url, enabled: true });
    });

    if (newFonts.length > 0) {
      const updatedFonts: FontInfo[] = [...fonts, ...newFonts];
      setFonts(updatedFonts);
      onFontListChange(updatedFonts);
    }
  };

  const handleFontUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = e.target.files;
    if (!files || files.length === 0) return;

    setPendingFiles(Array.from(files));
    setIsLicenseDialogOpen(true);
  };

  const handleLicenseCancel = (): void => {
    setIsLicenseDialogOpen(false);
    setPendingFiles(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLicenseConfirm = (): void => {
    if (pendingFiles && pendingFiles.length > 0) {
      processFontFiles(pendingFiles);
    }
    setIsLicenseDialogOpen(false);
    setPendingFiles(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleToggleFont = (idx: number) => {
    const updatedFonts = fonts.map((font, i) => (i === idx ? { ...font, enabled: !font.enabled } : font));
    setFonts(updatedFonts);
    onFontListChange(updatedFonts);
  };

  const toggleCollapsed = () => setIsCollapsed((prev) => !prev);

  const filteredFonts = fonts
    .map((f, i) => ({ ...f, idx: i }))
    .filter((f) => f.name.toLowerCase().includes(fontSearch.toLowerCase()));

  const handleOpen = () => setTimeout(() => searchInputRef.current?.focus(), 60);
  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  const handleSelectAll = (filtered: typeof filteredFonts) => {
    const allSelected = filtered.every((f) => f.enabled);
    const updatedFonts = fonts.map((f) =>
      filtered.some((ff) => ff.name === f.name) ? { ...f, enabled: !allSelected } : f
    );
    setFonts(updatedFonts);
    onFontListChange(updatedFonts);
  };

  return (
    <div className="font-management">
      <Dialog open={isLicenseDialogOpen} onClose={handleLicenseCancel} fullWidth maxWidth="sm">
        <DialogTitle>{t`Font License Confirmation`}</DialogTitle>
        <DialogContent>
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {t`By uploading a font to the system, I confirm that I hold a valid and legal license to use this font, and that I am not infringing any copyright or terms of use. I acknowledge that full responsibility for the use of the font lies solely with me.`}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLicenseCancel}>{t`Cancel`}</Button>
          <Button onClick={handleLicenseConfirm} variant="contained">{t`Confirm`}</Button>
        </DialogActions>
      </Dialog>
      <div className="settings-header" onClick={toggleCollapsed}>
        <span className="settings-header-title">{t`Font Management`}</span>
        <button
          type="button"
          className="settings-header-chevron"
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
        >
          <KeyboardArrowUpIcon className={`chevron-icon ${isCollapsed ? '' : 'expanded'}`} />
        </button>
      </div>

      {!isCollapsed && (
        <div className="font-management-group">
          <div className="setting-item upload-section">
            <label className="upload-label">
              <span className="upload-title">{t`Upload Fonts`}</span>
              <div className="tooltip-wrapper">
                <span className="tooltip-icon" style={{ marginLeft: '4px' }}>?</span>
                <div className="tooltip-text" style={{ paddingTop: '10px', height: '40px' }}>
                  {t`Supported formats: .ttf, .otf, .woff, .woff2`}
                </div>
              </div>
            </label>

            <div className="upload-controls">
              <input
                ref={fileInputRef}
                id="font-upload-input"
                type="file"
                accept=".ttf,.otf,.woff,.woff2"
                multiple
                onChange={handleFontUpload}
                style={{ display: 'none' }}
              />

              <Button
                variant="contained"
                onClick={() => fileInputRef.current?.click()}
                disableElevation
                disableRipple
                aria-label="Upload fonts"
                sx={{
                  width: 44,
                  height: 44,
                  minWidth: 44,
                  padding: 0,
                  borderRadius: 2,
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  border: '2px solid #1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.18s ease-in-out',
                  '&:hover': { backgroundColor: '#fff', color: '#1976d2', borderColor: '#1976d2', transform: 'scale(1.05)' },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  fill="currentColor"
                  style={{ display: 'block' }}
                >
                  <path d="M11.293 3.293a1 1 0 0 1 1.414 0l4 4a1 1 0 0 1-1.414 1.414L13 6.414V16a1 1 0 1 1-2 0V6.414L8.707 8.707a1 1 0 0 1-1.414-1.414l4-4Z" />
                  <path d="M6 17a1 1 0 1 0-2 0v.6C4 19.482 5.518 21 7.4 21h9.2c1.882 0 3.4-1.518 3.4-3.4V17a1 1 0 1 0-2 0v.6c0 .778-.622 1.4-1.4 1.4H7.4c-.778 0-1.4-.622-1.4-1.4V17Z" />
                </svg>
              </Button>
            </div>

            {loading && <div style={{ marginTop: "8px", color: "#1976d2" }}>{t`Uploading font...`}</div>}
            {error && <div style={{ marginTop: "8px", color: "red" }}>{error}</div>}
          </div>

          <div className="setting-item font-list-section">
            <label className="font-list-label">{t`Font List`}</label>

            <div className="tooltip-wrapper">
              <span className="tooltip-icon" style={{ marginLeft: '-4px' }}>?</span>
              <div
                className="tooltip-text-ratio"
                style={{ width: '30vh', maxWidth: 'none', whiteSpace: 'normal', wordWrap: 'break-word' }}
              >
                {t`Checked fonts will be available in the Block Properties section.`}
              </div>
            </div>

            <FormControl className="font-list-select" fullWidth>
              <Select
                sx={{ height: "40px", '& .MuiSelect-icon': { color: '#1976d2' } }}
                multiple
                value={fonts.filter(f => f.enabled).map(f => f.name)}
                renderValue={(selected) => (
                  <Box sx={{ height: '50%', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(selected as string[]).length ? (selected as string[]).join(', ') : t`None`}
                  </Box>
                )}
                onOpen={handleOpen}
                onClose={() => setFontSearch('')}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 260, borderRadius: 8, overflow: 'auto' } },
                  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                  transformOrigin: { vertical: 'top', horizontal: 'left' },
                  autoFocus: false,
                  MenuListProps: { onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation() },
                }}
              >
                {/* Search Input */}
                <MenuItem disableRipple onClick={stopPropagation} sx={{ px: 1, py: 0.5 }}>
                  <Box onMouseDown={(e) => e.stopPropagation()} sx={{ width: '100%' }}>
                    <TextField
                      inputRef={searchInputRef}
                      value={fontSearch}
                      onChange={(e) => setFontSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      placeholder={String(t`Search fonts...`)}
                      size="small"
                      variant="standard"
                      fullWidth
                      InputProps={{ disableUnderline: true, style: { fontSize: 13, paddingLeft: 6 } }}
                    />
                  </Box>
                </MenuItem>

                {filteredFonts.length > 0 && (
                  <MenuItem
                    disableRipple
                    onClick={(e) => { e.stopPropagation(); handleSelectAll(filteredFonts); }}
                    sx={{ marginTop: "4px", marginLeft: "28px", width: '75%', px: 1, py: 0.5, fontWeight: 500, cursor: 'pointer', borderRadius: 1, backgroundColor: '#e3f2fd', '&:hover': { backgroundColor: '#bbdefb' }, mb: 0.5, display: 'flex', justifyContent: 'center' }}
                  >
                    {filteredFonts.every(f => f.enabled) ? t`Unselect All` : t`Select All`}
                  </MenuItem>
                )}

                {fonts.length === 0 && <MenuItem disabled>{t`No fonts uploaded yet.`}</MenuItem>}
                {filteredFonts.length === 0 && fonts.length > 0 && <MenuItem disabled>{t`No fonts match your search.`}</MenuItem>}

                {filteredFonts.map((font) => (
                  <MenuItem
                    key={`${font.name}-${font.idx}`}
                    value={font.name}
                    onClick={(e) => { e.stopPropagation(); handleToggleFont(font.idx); }}
                    sx={{ borderRadius: 0 }}
                  >
                    <Checkbox
                      checked={font.enabled}
                      onClick={(e) => { e.stopPropagation(); handleToggleFont(font.idx); }}
                      disableRipple
                    />
                    <ListItemText primary={font.name} primaryTypographyProps={{ style: { fontFamily: font.name } }} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
      )}
    </div>
  );
};

export type { FontInfo };
export default FontManagement;
